/**
 * 007-inventory (research R5, FR-001 to FR-004, FR-012). No `packages/ui` counterpart — like
 * `empty-state.tsx`/`error-surface.tsx`, this states a rule rather than duplicating one, so it is
 * not part of `parity.test.tsx`.
 *
 * A labelled stock quantity, structural rather than editorial about three things a review would
 * otherwise have to catch on every screen:
 *
 * - FR-004/§16: Tally's stock figures will drift from the portal's. Every quantity carries the
 *   "portal is the authority" statement so nobody has to remember to add it on the eighth screen.
 * - FR-003/§24: a SKU with no conversion factor cannot show a piece equivalent. `unavailableReason`
 *   renders why, never a plausible-looking zero.
 * - FR-012: a negative balance renders as typed. `value` is never clamped here or by any caller —
 *   there is no `Math.max(0, value)` to remove.
 *
 * `compact` is the table-cell form. A column header already names the figure, and a statement
 * repeated under every cell of a grid is read as noise and then not read at all, so there the label
 * and the authority statement are carried by the figure itself — screen-reader text and a hover
 * title — rather than as two more lines per cell; a screen that lists stock states the authority
 * once, visibly, in its own header. Still present on every figure, never omitted. A negative
 * balance is red there, since in a column of figures it is the one a reader has to find.
 */
export function StockFigure({
  label,
  value,
  unit,
  unavailableReason,
  compact,
  className,
}: {
  label: string;
  /** The API's own figure, pre-formatted. Rendered as-is — negative stays negative (FR-012). */
  value?: number | string;
  unit?: string;
  /** Set instead of `value` when the API could not derive this figure (FR-003). Never render `0`. */
  unavailableReason?: string;
  /** The table-cell form — the authority statement as accessible text and a title, not a caption. */
  compact?: boolean;
  className?: string;
}) {
  const authority = "Portal is the authority for this figure";

  if (compact) {
    // Display only: the formatted figure's own sign, read, not computed.
    const negative = String(value ?? "").trim().startsWith("-");
    return (
      // `relative`: contains the absolutely-positioned `sr-only` spans, which otherwise escape the
      // table's scroll container and widen the whole page on a narrow screen.
      <span data-slot="stock-figure" title={authority} className={`relative inline-flex flex-col${className ? ` ${className}` : ""}`}>
        <span className="sr-only">{label}: </span>
        {unavailableReason ? (
          <span className="text-xs italic text-muted-foreground">Unavailable — {unavailableReason}</span>
        ) : (
          <span className={`whitespace-nowrap font-mono text-sm font-semibold tabular-nums${negative ? " text-red-dark" : ""}`}>
            {value}
            {unit ? ` ${unit}` : ""}
          </span>
        )}
        <span className="sr-only"> ({authority})</span>
      </span>
    );
  }

  return (
    <div data-slot="stock-figure" className={`flex flex-col gap-0.5${className ? ` ${className}` : ""}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {unavailableReason ? (
        <span className="text-sm italic text-muted-foreground">Unavailable — {unavailableReason}</span>
      ) : (
        <span className="font-mono text-sm font-semibold tabular-nums">
          {value}
          {unit ? ` ${unit}` : ""}
        </span>
      )}
      <span className="text-xs text-muted-foreground">{authority}</span>
    </div>
  );
}
